import React, { useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { userService } from '@/shared/services/userService';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import { institutionFromEmail } from '@/shared/utils/institution';
import usePageTitle from '@/shared/hooks/usePageTitle';

// Shown both as the read-only institution line when the email doesn't match
// a campus domain and as the error on a 403 INSTITUTION_UNKNOWN: the server
// derives from the same four domains, so the two cases describe the same
// fact.
const DOMAIN_ERROR = 'Your email domain is not one of the campus domains.';

function CreateProfile() {
    usePageTitle('Complete your profile');
    const { currentUser, refreshProfile } = useAuth();
    // A federated sign-in (Microsoft) supplies a display name; an email and
    // password account has none. Either way the field stays editable.
    const [formData, setFormData] = useState({
        name: currentUser?.displayName || '',
        department: '',
        phone: '',
    });
    const [accepted, setAccepted] = useState(false);
    const [termsError, setTermsError] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const institution = institutionFromEmail(currentUser?.email);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    async function handleSubmit(e) {
        e.preventDefault();

        if (!formData.name.trim()) {
            return setError('Name is required');
        }

        if (!accepted) {
            setError('');
            setTermsError(
                'Agree to the Terms of use and Privacy statement to continue.'
            );
            return document.getElementById('accept-terms')?.focus();
        }

        try {
            setError('');
            setTermsError('');
            setLoading(true);

            await userService.createProfile({
                name: formData.name.trim(),
                department: formData.department.trim() || undefined,
                phone: formData.phone.trim() || undefined,
                acceptTerms: true,
            });
            refreshProfile();
            navigate('/listings');
        } catch (err) {
            if (err.response?.status === 409) {
                setError('A profile already exists for this account.');
            } else if (err.response?.data?.code === 'INSTITUTION_UNKNOWN') {
                setError(DOMAIN_ERROR);
            } else {
                setError('Failed to create profile. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col md={8} lg={6}>
                    <Card>
                        <Card.Body className="p-4">
                            <h1 className="h2 text-center mb-2">
                                Complete Your Profile
                            </h1>
                            {currentUser?.email && (
                                <p className="text-muted text-center mb-2">
                                    Signed in as {currentUser.email}
                                </p>
                            )}
                            <p className="text-center mb-4">
                                {institution ? (
                                    <>
                                        Institution:{' '}
                                        <InstitutionLogo
                                            institution={institution}
                                        />
                                    </>
                                ) : (
                                    DOMAIN_ERROR
                                )}
                            </p>

                            {error && <Alert variant="danger">{error}</Alert>}

                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Full Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        autoComplete="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter your full name"
                                        required
                                    />
                                </Form.Group>

                                <Form.Group
                                    className="mb-3"
                                    controlId="department"
                                >
                                    <Form.Label>Department</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        placeholder="e.g. Computer Science"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="phone">
                                    <Form.Label>Phone</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="phone"
                                        autoComplete="tel"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="e.g. 303-555-0100"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="accept-terms"
                                        checked={accepted}
                                        onChange={(e) =>
                                            setAccepted(e.target.checked)
                                        }
                                        isInvalid={Boolean(termsError)}
                                        aria-required="true"
                                        aria-invalid={
                                            termsError ? 'true' : undefined
                                        }
                                        aria-describedby={
                                            termsError
                                                ? 'accept-terms-feedback'
                                                : undefined
                                        }
                                        label={
                                            <>
                                                I agree to the{' '}
                                                <Link to="/terms">
                                                    Terms of use
                                                </Link>{' '}
                                                and the{' '}
                                                <Link to="/privacy">
                                                    Privacy statement
                                                </Link>
                                            </>
                                        }
                                    />
                                    {termsError && (
                                        <div
                                            id="accept-terms-feedback"
                                            className="invalid-feedback d-block"
                                        >
                                            {termsError}
                                        </div>
                                    )}
                                </Form.Group>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-100 mt-2"
                                    disabled={loading}
                                >
                                    {loading
                                        ? 'Creating Profile...'
                                        : 'Complete Profile'}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default CreateProfile;
